import { router } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import actionCableConsumer from '../lib/actionCableConsumer';

export const useTripUpdates = (initialTrips, selectedDate) => {
  const [trips, setTrips] = useState(initialTrips);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [lastUpdate, setLastUpdate] = useState(null);
  const subscriptionRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const subscriptionId = useRef(`trip_updates_${Date.now()}_${Math.random()}`);
  const disconnectTimeoutRef = useRef(null);

  // Função para verificar se é hoje
  const isToday = () => {
    const today = new Date().toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).split('/').reverse().join('-'); // Converte DD/MM/YYYY para YYYY-MM-DD
    return selectedDate === today;
  };

  // Função para sortear trips
  const statePriority = {
    stopped: -3,
    off_duty: -2,
    completed: -1,
    finished: 0,
    estimated: 1,
    picture_received: 2,
    started: 3,
    on_duty: 4,
    pending: 5,
  };

  const sortTrips = (tripList) => {
    return [...(tripList ?? [])].sort((a, b) => {
      // 1. Ordenação primária por prioridade de estado
      const priorityA = statePriority[a.state] ?? 99;
      const priorityB = statePriority[b.state] ?? 99;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // 2. Ordenação secundária por transportadora (alfabética, valores vazios primeiro)
      const carrierA = a.carrier_name || '';
      const carrierB = b.carrier_name || '';

      if (carrierA !== carrierB) {
        // Valores vazios vão para o começo
        if (!carrierA && carrierB) return -1;
        if (carrierA && !carrierB) return 1;
        return carrierA.localeCompare(carrierB);
      }

      // 3. Ordenação terciária por motorista (alfabética, valores vazios primeiro)
      const userA = a.user_name || '';
      const userB = b.user_name || '';

      if (userA !== userB) {
        // Valores vazios vão para o começo
        if (!userA && userB) return -1;
        if (userA && !userB) return 1;
        return userA.localeCompare(userB);
      }

      // 4. Ordenação quaternária por sequence_indicator (crescente)
      const seqA = parseInt(a.sequence_indicator) || 0;
      const seqB = parseInt(b.sequence_indicator) || 0;

      return seqA - seqB;
    });
  };

  // Função para atualizar trips baseado no tipo de broadcast
  const updateTrips = (data) => {
    const { type, trip } = data;

    setTrips(currentTrips => {
      let updatedTrips = [...currentTrips];

      switch (type) {
        case 'trip_created':
          // Adicionar nova trip se não existir
          if (!updatedTrips.find(t => t.id === trip.id)) {
            updatedTrips.push(trip);
          }
          break;

        case 'trip_updated':
          // Atualizar trip existente
          const updateIndex = updatedTrips.findIndex(t => t.id === trip.id);
          if (updateIndex !== -1) {
            updatedTrips[updateIndex] = trip;
          }
          break;

        case 'trip_removed':
          // Remover trip
          updatedTrips = updatedTrips.filter(t => t.id !== trip.id);
          break;

        case 'trip_state_changed':
          // Atualizar estado da trip
          const stateIndex = updatedTrips.findIndex(t => t.id === trip.id);
          if (stateIndex !== -1) {
            updatedTrips[stateIndex] = { ...updatedTrips[stateIndex], ...trip };
          }
          break;

        default:
          console.warn('Unknown trip update type:', type);
      }

      return sortTrips(updatedTrips);
    });

    setLastUpdate(new Date());
  };

  // Função para lidar com desconexão prolongada
  const handleDisconnectTimeout = () => {
    if (isUnmountedRef.current) return;

    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
    }

    disconnectTimeoutRef.current = setTimeout(() => {
      if (isUnmountedRef.current) return;
      console.log('Disconnected for more than 1 minute, reloading page...');
      router.reload();
    }, 60000); // 1 minuto
  };

  // Função para limpar timeout de desconexão
  const clearDisconnectTimeout = () => {
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
  };

  // Função para inicializar conexão usando consumer global
  const initializeConnection = () => {
    if (isUnmountedRef.current) return;

    // Registrar callback para mudanças de status
    actionCableConsumer.onStatusChange(subscriptionId.current, (status) => {
      if (isUnmountedRef.current) return;
      setConnectionStatus(status);
    });

    // Criar subscription usando consumer global
    const subscription = actionCableConsumer.createSubscription(
      'TripsChannel',
      subscriptionId.current,
      {
        connected() {
          if (isUnmountedRef.current) return;
          console.log('Connected to TripsChannel via global consumer');
          clearDisconnectTimeout();
        },

        disconnected() {
          if (isUnmountedRef.current) return;
          console.log('Disconnected from TripsChannel');
          handleDisconnectTimeout();
        },

        received(data) {
          if (isUnmountedRef.current) return;
          console.log('Received data from TripsChannel:', data);

          // Só atualizar se for hoje
          if (isToday()) {
            updateTrips(data);
          }
        },

        rejected() {
          if (isUnmountedRef.current) return;
          console.log('TripsChannel subscription rejected');
        }
      }
    );

    subscriptionRef.current = subscription;
  };

  // Função para cleanup completo
  const cleanup = () => {
    console.log('Cleaning up useTripUpdates...');

    clearDisconnectTimeout();

    // Remover subscription específica
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    // Remover callback de status
    actionCableConsumer.removeStatusCallback(subscriptionId.current);
  };

  // Efeito para gerenciar conexão
  useEffect(() => {
    // Só conectar se for hoje
    if (isToday()) {
      initializeConnection();
    }

    // Cleanup na unmount
    return () => {
      isUnmountedRef.current = true;
      cleanup();
    };
  }, [selectedDate]);

  // Atualizar trips quando initialTrips mudar
  useEffect(() => {
    setTrips(sortTrips(initialTrips));
  }, [initialTrips]);

  return {
    trips: sortTrips(trips),
    connectionStatus,
    lastUpdate,
    isRealTimeEnabled: isToday()
  };
}; 